import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser';
import { useNotificationStack } from '../../../app/hooks/useNotificationStack';
import {
  usePostCustomerMutation,
  usePutCustomerCustomerMutation,
} from '../../../app/redux-fetch/apiQuery';
import SubmitButtons from '../../../components/Buttons/SubmitButtons';
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid';
import { ContactCategoryTypeId } from '../../../types/common';
import { useSideModalContext } from '../../Assets/modals/SideModalContext';
import ContactTable from '../../Contacts/ContactTable';
import Documents from '../../Documents/Documents';
import AddressDetails from '../../Locations/components/AddressDetails';
import DefaultValues from '../../Locations/components/DefaultValues';
import { ICustomer, ILocation } from '../../types';
import { CustomerFormData, customerSchema } from './Components/validationSchema';
import CustomerDetail from './CustomerDetail';
import messages from './messages';

type CustomerFormProps = {
  customer: ICustomer
  isDisabled: boolean
}

const CustomerForm = (props: CustomerFormProps) => {
  const { customer, isDisabled } = props
  const { tenantId } = useLoggedInUser()

  const { onClose } = useSideModalContext()
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()

  const isNew = !customer.id

  const methods = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: customer as CustomerFormData,
  })

  const [createCustomer] = usePostCustomerMutation()
  const [putCustomer] = usePutCustomerCustomerMutation()

  const onSubmit = async (values: CustomerFormData) => {
    try {
      if (!values.id) {
        const location = {
          id: 0,
          addressId: 0,
          customerId: 0,
          name: `${values.name}-${uuidv4()}`,
          tenantId,
          locationTypeId: 0,
          address: values.address,
          defaultTransportModeId: values.defaultTransportModeId,
          defaultCurrencyId: values.defaultCurrencyId,
          lat: values.lat,
          lng: values.lng,
          geofence: values.geofence,
        } as ILocation
        await createCustomer({ tenantId, body: { ...values, location: location } as ICustomer }).unwrap()
        enqueueSuccess(messages.newCustomerCreated)
      } else {
        const location = {
          id: values.locationId,
          addressId: values.addressId,
          customerId: values.id,
          name: values.locationName,
          tenantId,
          locationTypeId: 0,
          address: values.address,
          defaultTransportModeId: values.defaultTransportModeId,
          defaultCurrencyId: values.defaultCurrencyId,
          lat: values.lat,
          lng: values.lng,
          geofence: values.geofence,
          revision: customer.location?.revision,
          validFrom: customer.location?.validFrom,
        } as ILocation
        await putCustomer({ tenantId, body: { ...values, location: location } as ICustomer }).unwrap()
        enqueueSuccess(messages.customerRecordUpdated)
      }
      onClose()
    } catch (error) {
      if (!values.id) {
        enqueueFailure(messages.customerCreateFailed)
      } else {
        enqueueFailure(messages.customerUpdateFailed)
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <DefaultSpacingGrid container sx={{ p: '48px', mt: '48px', mb: '48px' }}>
          <Grid display='flex' flexDirection='row' gap={4} width='100%'>
            <Grid display='flex' flexDirection='column' width='50%'>
              <CustomerDetail customer={customer} isDisabled={isDisabled} isNew={isNew} />
              <DefaultValues isDisabled={isDisabled} />
              <ContactTable
                isDisabled={isDisabled}
                skipFetch={isDisabled}
                contactCategoryId={ContactCategoryTypeId.customer}
              />
            </Grid>
            <Grid display='flex' flexDirection='column' width='50%'>
              <Grid item xs={12}>
                <AddressDetails isDisabled={isDisabled} />
              </Grid>
              <Grid item xs={12}>
                  <Documents
                    categoryId={1}
                    entityId={customer.id || undefined}
                    isDisabled={isDisabled}
                    skipFetch={isDisabled}
                    tenantId={customer.tenantId ?? tenantId}
                    customerId={customer.id || undefined}
                  />
              </Grid>
            </Grid>
          </Grid>
        </DefaultSpacingGrid>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            padding: '16px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
          }}
        >
          {!isDisabled && <SubmitButtons create={isNew} isEnabled={true} onCancel={onClose} submitForm={methods.handleSubmit(onSubmit)} />}
        </Box>
      </form>
    </FormProvider>
  )
}

export default CustomerForm
